import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { format, formatDistanceToNow } from "date-fns";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  Calendar,
  Activity,
  MousePointerClick,
  UserCheck,
  Package,
  AlertCircle,
  Loader2,
} from "lucide-react";

interface PromoCodeStatsProps {
  promoCode: {
    id: number;
    code: string;
    description?: string;
    discountPercent: number;
    status: string;
    timesRedeemed: number;
    totalRevenue: string;
    totalCommissionEarned: string;
    createdAt: string;
    expiresAt?: string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CodeStats {
  overview: {
    totalClicks: number;
    totalSignups: number;
    totalConversions: number;
    conversionRate: number;
    totalRevenue: number;
    totalCommission: number;
    averageOrderValue: number;
  };
  usageOverTime: Array<{
    date: string;
    clicks: number;
    signups: number;
    conversions: number;
    revenue: number;
  }>;
  recentUses: Array<{
    id: string;
    customerEmail?: string;
    orderValue: number;
    commission: number;
    usedAt: string;
    status: string;
  }>;
  topProducts: Array<{
    productName: string;
    purchases: number;
    revenue: number;
  }>;
}

export default function PromoCodeStats({ promoCode, open, onOpenChange }: PromoCodeStatsProps) {
  const { data: stats, isLoading, error } = useQuery<CodeStats>({
    queryKey: [`/api/influencer/promo-codes/${promoCode.id}/stats`],
    enabled: open && !!promoCode.id,
    retry: 1,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              Promo Code Performance: {promoCode.code}
            </div>
            <Badge variant={promoCode.status === "active" ? "default" : "secondary"}>
              {promoCode.status}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {promoCode.description || `Detailed analytics and usage statistics for ${promoCode.code}`}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-8rem)] pr-4">
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-64" />
              <Skeleton className="h-48" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-muted-foreground">Failed to load statistics</p>
              </div>
            </div>
          )}

          {stats && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-purple-200 dark:border-purple-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MousePointerClick className="w-4 h-4" />
                      Total Clicks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="text-total-clicks">
                      {stats.overview.totalClicks.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-200 dark:border-blue-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Conversions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="text-total-conversions">
                      {stats.overview.totalConversions}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatPercent(stats.overview.conversionRate)} conversion rate
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-green-200 dark:border-green-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Total Revenue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="text-total-revenue">
                      {formatCurrency(stats.overview.totalRevenue)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatCurrency(stats.overview.totalCommission)} earned
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Usage Over Time Chart */}
              {stats.usageOverTime && stats.usageOverTime.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Usage Over Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={stats.usageOverTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => format(new Date(value), "MMM d")}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(value) => format(new Date(value), "PPP")}
                          formatter={(value: number) => value.toLocaleString()}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="clicks"
                          stackId="1"
                          stroke="#8b5cf6"
                          fill="#8b5cf6"
                          fillOpacity={0.6}
                        />
                        <Area
                          type="monotone"
                          dataKey="signups"
                          stackId="1"
                          stroke="#3b82f6"
                          fill="#3b82f6"
                          fillOpacity={0.6}
                        />
                        <Area
                          type="monotone"
                          dataKey="conversions"
                          stackId="1"
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.6}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Revenue Chart */}
              {stats.usageOverTime && stats.usageOverTime.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Revenue Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={stats.usageOverTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => format(new Date(value), "MMM d")}
                        />
                        <YAxis tickFormatter={(value) => `$${value}`} />
                        <Tooltip
                          labelFormatter={(value) => format(new Date(value), "PPP")}
                          formatter={(value: number) => formatCurrency(value)}
                        />
                        <Line
                          type="monotone"
                          dataKey="revenue"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={{ fill: "#10b981", r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Recent Uses */}
              {stats.recentUses && stats.recentUses.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Recent Uses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {stats.recentUses.slice(0, 5).map((use) => (
                        <div
                          key={use.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                          data-testid={`recent-use-${use.id}`}
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {use.customerEmail || "Anonymous Customer"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(use.usedAt), { addSuffix: true })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">
                              {formatCurrency(use.orderValue)}
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400">
                              +{formatCurrency(use.commission)}
                            </p>
                          </div>
                          <Badge
                            variant={use.status === "completed" ? "default" : "secondary"}
                            className="ml-2"
                          >
                            {use.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Code Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Code Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Discount</span>
                    <span className="font-medium">{promoCode.discountPercent}%</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Created</span>
                    <span className="font-medium">
                      {format(new Date(promoCode.createdAt), "PPP")}
                    </span>
                  </div>
                  <Separator />
                  {promoCode.expiresAt && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Expires</span>
                        <span className="font-medium">
                          {format(new Date(promoCode.expiresAt), "PPP")}
                        </span>
                      </div>
                      <Separator />
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Uses</span>
                    <span className="font-medium">{promoCode.timesRedeemed}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Avg Order Value</span>
                    <span className="font-medium">
                      {formatCurrency(stats.overview.averageOrderValue)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}